import { Spinner } from "@/components/ui/spinner";
import { useEffect, useRef, useState } from "react";
import { X, UploadCloud, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { ExpenseCategory, ErrorCode, dayjs } from "@boo/shared";
import type { SlipParsedDraftDto } from "@boo/shared";
import {
  parseSlipWeb,
  confirmSlipWeb,
  MAX_SLIP_BYTES,
  SLIP_MIME_TYPES,
  ApiError,
} from "@/services/expenseApi";
import { useAppStore } from "../../store/useAppStore";
import { CATEGORY_LABELS } from "../../types";
import AmountInput from "@/components/AmountInput";
import {
  refreshSuggestions,
  useAccountStore,
} from "@/features/accounts/store/useAccountStore";

const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  ExpenseCategory.FOOD,
  ExpenseCategory.TRANSPORT,
  ExpenseCategory.SHOPPING,
  ExpenseCategory.BILLS,
  ExpenseCategory.ENTERTAINMENT,
  ExpenseCategory.HEALTH,
  ExpenseCategory.HOUSING,
  ExpenseCategory.FINANCIAL,
  ExpenseCategory.INVESTMENT,
  ExpenseCategory.OTHER,
];

const inputClass =
  "w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border";

type Mode =
  | "idle"
  | "uploading"
  | "review"
  | "parse-failed"
  | "manual"
  | "error";

const emptyDraft = (): SlipParsedDraftDto => ({
  amount: 0,
  title: "",
  date: dayjs().format("YYYY-MM-DD"),
  category: ExpenseCategory.OTHER,
  note: "",
  accountWarning: false,
  isTransfer: false,
});

interface QrSlipUploadModalProps {
  onClose: () => void;
}

export default function QrSlipUploadModal({ onClose }: QrSlipUploadModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<Mode>("idle");
  const [draft, setDraft] = useState<SlipParsedDraftDto | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again re-triggers change.
    e.target.value = "";
    if (!file) return;

    if (!SLIP_MIME_TYPES.includes(file.type)) {
      toast.error("Unsupported file type. Use JPEG, PNG, or WEBP.");
      return;
    }
    if (file.size > MAX_SLIP_BYTES) {
      toast.error("File exceeds the 5 MB limit");
      return;
    }

    setMode("uploading");
    try {
      const result = await parseSlipWeb(file);
      setDraft(result);
      setMode("review");
    } catch (err) {
      // A parse failure (422 / SLIP_PARSE_FAILED) offers manual entry as a
      // fallback. Network or unexpected errors keep the retry path.
      if (
        err instanceof ApiError &&
        (err.errorCode === ErrorCode.SLIP_PARSE_FAILED || err.status === 422)
      ) {
        setMode("parse-failed");
        return;
      }
      setErrorMsg(
        err instanceof ApiError
          ? err.message
          : "Could not read this slip. Please try again.",
      );
      setMode("error");
    }
  };

  const reset = () => {
    setDraft(null);
    setErrorMsg("");
    setMode("idle");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm mx-4 rounded-[16px] bg-card border-[0.5px] border-border shadow-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[15px] font-semibold text-foreground">
            Upload QR slip
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleFile}
        />

        {mode === "idle" && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full flex flex-col items-center gap-2.5 py-10 rounded-[12px] border border-dashed border-border text-center transition-colors hover:bg-[var(--surface-muted)] cursor-pointer"
          >
            <UploadCloud className="w-7 h-7 text-muted-foreground" />
            <span className="text-[13px] font-medium text-foreground">
              Choose a slip image
            </span>
            <span className="text-[11px] text-muted-foreground">
              JPEG, PNG, or WEBP · max 5 MB
            </span>
          </button>
        )}

        {mode === "uploading" && (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <Spinner className="size-7 text-muted-foreground" />
            <span className="text-[13px] text-muted-foreground">
              Reading your slip…
            </span>
          </div>
        )}

        {mode === "review" && draft && (
          <SlipReviewForm draft={draft} onClose={onClose} />
        )}

        {mode === "parse-failed" && (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertCircle className="w-7 h-7 text-amber-500" />
            <span className="text-[13px] text-foreground">
              Could not read slip — enter details manually
            </span>
            <div className="flex items-center gap-2 mt-1">
              <button
                type="button"
                onClick={() => setMode("manual")}
                className="text-[13px] font-medium px-4 py-2 rounded-[10px] bg-primary text-primary-foreground hover:opacity-90 transition-opacity cursor-pointer"
              >
                Enter Manually
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-[13px] font-medium px-4 py-2 rounded-[10px] bg-transparent text-[var(--text-secondary)] border border-border hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {mode === "manual" && (
          <SlipReviewForm draft={emptyDraft()} onClose={onClose} />
        )}

        {mode === "error" && (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertCircle className="w-7 h-7 text-red-500" />
            <span className="text-[13px] text-foreground">
              {errorMsg || "Could not read this slip."}
            </span>
            <div className="flex items-center gap-2 mt-1">
              <button
                type="button"
                onClick={reset}
                className="text-[13px] font-medium px-4 py-2 rounded-[10px] bg-primary text-primary-foreground hover:opacity-90 transition-opacity cursor-pointer"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-[13px] font-medium px-4 py-2 rounded-[10px] bg-transparent text-[var(--text-secondary)] border border-border hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface SlipReviewFormProps {
  draft: SlipParsedDraftDto;
  onClose: () => void;
}

function SlipReviewForm({ draft, onClose }: SlipReviewFormProps) {
  const [amount, setAmount] = useState(
    draft.amount ? String(draft.amount) : "",
  );
  const [title, setTitle] = useState(draft.title);
  const [date, setDate] = useState(draft.date);
  const [category, setCategory] = useState<ExpenseCategory>(draft.category);
  const [note, setNote] = useState(draft.note);
  const [isTransfer, setIsTransfer] = useState(draft.isTransfer);
  const accounts = useAccountStore((s) => s.accounts);
  const ensureAccounts = useAccountStore((s) => s.ensureAccounts);
  const activeAccounts = accounts.filter((a) => a.isActive);
  const [pickedAccountId, setPickedAccountId] = useState<string | null>(null);
  // Until the user picks: the slip's own account, else the default account.
  const accountId =
    pickedAccountId ??
    draft.accountId ??
    accounts.find((a) => a.isActive && a.isDefault)?.id ??
    "";
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        await ensureAccounts();
      } catch {
        // The picker is optional; the form works without accounts.
      }
    }
    void load();
  }, [ensureAccounts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError("Enter a valid amount.");
      return;
    }
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await confirmSlipWeb({
        amount: amountNum,
        title: title.trim(),
        date,
        category,
        note: note.trim(),
        isTransfer,
        accountId: accountId || undefined,
      });
      toast.success("Transaction added");
      onClose();
      const { viewYear, viewMonth, loadMonth } = useAppStore.getState();
      await loadMonth(viewYear, viewMonth);
      await refreshSuggestions();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {draft.accountWarning && (
        <div className="flex items-start gap-2 rounded-[8px] border-[0.5px] border-amber-500/40 bg-amber-500/10 px-3 py-2">
          <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
          <span className="text-[12px] text-foreground">
            This slip doesn't match any of your configured bank accounts. Save
            only if it belongs to you.
          </span>
        </div>
      )}

      {/* Amount */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[12px] text-muted-foreground font-medium">
          Amount (฿)
        </label>
        <AmountInput
          className={`${inputClass} [font-variant-numeric:tabular-nums]`}
          value={amount}
          onChange={setAmount}
        />
      </div>

      {/* Title */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[12px] text-muted-foreground font-medium">
          Title
        </label>
        <input
          className={inputClass}
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      {/* Date */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[12px] text-muted-foreground font-medium">
          Date
        </label>
        <input
          type="date"
          className={`${inputClass} [font-variant-numeric:tabular-nums]`}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      {/* Transfer between own accounts */}
      <label className="flex items-center gap-2 text-[12px] text-foreground cursor-pointer">
        <input
          type="checkbox"
          checked={isTransfer}
          onChange={(e) => setIsTransfer(e.target.checked)}
        />
        Transfer between my accounts (not counted as spending)
      </label>

      {/* Category */}
      {!isTransfer && (
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] text-muted-foreground font-medium">
            Category
          </label>
          <select
            className={inputClass}
            value={category}
            onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
          >
            {EXPENSE_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_LABELS[cat]}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Account */}
      {activeAccounts.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] text-muted-foreground font-medium">
            Account
          </label>
          <select
            className={inputClass}
            value={accountId}
            onChange={(e) => setPickedAccountId(e.target.value)}
          >
            <option value="">Unassigned</option>
            {activeAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
                {account.digits ? ` ••${account.digits}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Note */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[12px] text-muted-foreground font-medium">
          Note
        </label>
        <textarea
          className={`${inputClass} resize-none`}
          rows={2}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      {error && <span className="text-[12px] text-red-500">{error}</span>}

      <div className="flex items-center justify-end gap-2 mt-1">
        <button
          type="button"
          onClick={onClose}
          className="text-[13px] font-medium px-4 py-2 rounded-[10px] bg-transparent text-[var(--text-secondary)] border border-border hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-[10px] bg-primary text-primary-foreground hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving && <Spinner className="size-3.5" />}
          {saving ? "Saving…" : "Confirm"}
        </button>
      </div>
    </form>
  );
}
